import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { useCartCtx } from '../App';
import { useCurrency } from '../hooks/useCurrency';
import { useRewardConfig } from '../hooks/useRewardConfig';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { useCategories } from '../hooks/useCategories';

export default function ShopPage() {
  const { fmt } = useCurrency();
  const rewards = useRewardConfig();
  const { label: categoryLabel } = useCategories();
  const [products, setProducts]         = useState([]);
  const [pagination, setPagination]     = useState({});
  const [loading, setLoading]           = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const { addItem } = useCartCtx();

  const search   = searchParams.get('search') || '';
  const category = searchParams.get('category') || '';
  const page     = parseInt(searchParams.get('page') || '1');

  useEffect(() => {
    setLoading(true);
    api.get('/products', { params: { page, search, limit: 12, ...(category ? { category } : {}) } })
      .then(res => { setProducts(res.data.data); setPagination(res.data.pagination); })
      .catch(() => toast.error('Failed to load products'))
      .finally(() => setLoading(false));
  }, [page, search, category]);

  const handleSearch = (e) => {
    e.preventDefault();
    const q = e.target.search.value;
    setSearchParams({ ...(q ? { search: q } : {}), ...(category ? { category } : {}) });
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-bold">
            {category ? categoryLabel(category) : 'Shop the Collection'}
          </h1>
          {category && (
            <button
              type="button"
              onClick={() => setSearchParams(search ? { search } : {})}
              className="mt-2 text-sm text-tea-700 hover:text-tea-800 underline underline-offset-2 dark:text-tea-400"
            >
              ← All products
            </button>
          )}
          <p className="text-sm text-gray-600 mt-2 max-w-2xl">
            Advanced topical skincare powered by GHK-CU peptides and bee venom, blended with
            ethically sourced, boho-luxe botanicals. <strong className="text-tea-700">Clean, vegan &amp;
            cruelty-free.</strong> Free shipping on orders over {fmt(rewards.freeShippingThresholdCents)}.
          </p>
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="flex gap-3 mb-8 max-w-md">
          <input name="search" defaultValue={search} placeholder="Search products (e.g. collagen, eye, scalp)..."
            className="input-field flex-1" />
          <button type="submit" className="btn-primary px-6">Search</button>
        </form>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="card animate-pulse h-64 bg-gray-100" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <p className="text-gray-500 text-center py-20">No products found.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {products.map(product => (
              <div key={product.id} className="card hover:shadow-md transition-shadow flex flex-col">
                <Link to={`/shop/${product.slug}`}>
                  <div className="w-full h-48 bg-tea-50 rounded-lg mb-4 overflow-hidden flex items-center justify-center">
                    {product.imageUrl ? (
                      <img
                        src={product.imageUrl}
                        alt={product.name}
                        className="w-full h-full object-cover"
                        onError={e => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'flex'; }}
                      />
                    ) : null}
                    <div style={{ display: product.imageUrl ? 'none' : 'flex' }} className="w-full h-full items-center justify-center">
                      <svg viewBox="0 0 64 64" className="w-24 h-24 text-tea-600" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <rect x="22" y="6" width="20" height="10" rx="2" />
                        <rect x="20" y="16" width="24" height="6" />
                        <path d="M22 22h20l-2 34a4 4 0 0 1-4 4h-8a4 4 0 0 1-4-4z" />
                        <path d="M26 30h12M26 40h12" strokeOpacity="0.4" />
                      </svg>
                    </div>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">{product.name}</h3>
                </Link>
                <p className="text-gray-500 text-sm mb-3 flex-1 line-clamp-2">{product.description}</p>
                {product.variants?.[0] && (
                  <div className="mt-auto">
                    <div className="flex items-center justify-between">
                      <span className="text-tea-700 font-bold text-lg">
                        {fmt(product.variants[0].priceInCents)}
                      </span>
                      <button
                        onClick={() => { addItem(product.variants[0], product); toast.success('Added to cart!'); }}
                        className="btn-primary text-sm px-4 py-2">
                        Add to Cart
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex justify-center gap-2 mt-10">
            {[...Array(pagination.pages)].map((_, i) => (
              <button key={i}
                onClick={() => setSearchParams({ page: i + 1, ...(search ? { search } : {}) })}
                className={`px-4 py-2 rounded-lg border font-medium transition-colors
                  ${page === i + 1
                    ? 'bg-tea-600 text-white border-tea-600'
                    : 'border-gray-300 hover:border-tea-400'}`}>
                {i + 1}
              </button>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
